import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Trash2, Loader2, User, Download } from "lucide-react";
import { authApi } from "@/services/api";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/format";
import { downloadMyData } from "@/lib/dataExport";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function PortalAccount() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [exporting, setExporting] = useState("");

  const doLogout = () => { logout(); navigate("/portal/login", { replace: true }); };

  const doExport = async (fmt) => {
    setExporting(fmt);
    try { await downloadMyData(fmt); toast.success("Your data download has started"); }
    catch (e) { toast.error(apiError(e, "Could not export your data")); }
    finally { setExporting(""); }
  };

  const doDelete = async () => {
    if (confirm.trim().toUpperCase() !== "DELETE") { toast.error('Type DELETE to confirm.'); return; }
    if (!password) { toast.error("Enter your password."); return; }
    setBusy(true);
    try {
      await authApi.deleteAccount({ confirmation: confirm.trim().toUpperCase(), password });
      toast.success("Your account has been deleted");
      logout();
      navigate("/portal/login", { replace: true });
    } catch (e) { toast.error(apiError(e, "Could not delete account")); setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <PageHeader title="My account" subtitle="Manage your portal login" testId="portal-account-header" />
      <Card className="space-y-2 p-5" data-testid="portal-account-info">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-500"><User className="h-5 w-5" /></div>
          <div>
            <p className="font-semibold text-slate-800">{user?.name || "Portal user"}</p>
            <p className="text-sm text-slate-500">{user?.email}</p>
          </div>
        </div>
      </Card>

      <Button variant="outline" className="w-full" onClick={doLogout} data-testid="portal-signout">
        <LogOut className="mr-2 h-4 w-4" /> Sign out
      </Button>

      <Card className="space-y-3 p-5" data-testid="portal-export">
        <h3 className="font-semibold text-slate-800">Download my data</h3>
        <p className="text-sm text-slate-500">Get a copy of the personal data we hold about you.</p>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => doExport("json")} disabled={!!exporting} data-testid="portal-export-json">
            {exporting === "json" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} JSON
          </Button>
          <Button variant="outline" onClick={() => doExport("csv")} disabled={!!exporting} data-testid="portal-export-csv">
            {exporting === "csv" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} CSV
          </Button>
        </div>
      </Card>

      <Card className="space-y-3 border-red-100 p-5">
        <h3 className="font-semibold text-red-700">Delete account</h3>
        <p className="text-sm text-slate-500">Permanently remove your portal login. This cannot be undone.</p>
        {!showDelete ? (
          <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => setShowDelete(true)} data-testid="portal-delete-start">
            <Trash2 className="mr-2 h-4 w-4" /> Delete my account
          </Button>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Type DELETE to confirm</Label>
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE" data-testid="portal-delete-confirm" />
            </div>
            <div className="space-y-1.5">
              <Label>Your password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="portal-delete-password" />
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setShowDelete(false)} disabled={busy}>Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700" onClick={doDelete} disabled={busy} data-testid="portal-delete-confirm-btn">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />} Permanently delete
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
